import { Text, type StyleProp, type TextStyle } from "react-native";
import { useTranslation } from "react-i18next";
import {
  COMEBACK_EASE,
  LAYOFF_WEEKS,
  layoffLabel,
  muscleKey,
  type CatalogMuscleSlug,
  type LayoffWeeks,
  type ModelOptionChoices,
} from "@heraion/shared";

import type { ModelAxes } from "../Library/modelCatalog";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import type { UiTheme } from "../theme";
import { politeLive } from "./ModelFocusRow";

const NO_BREAK = 0;
const NO_MUSCLE = "none";

// #479: the options a model admits, each a row of chips with «none» first and
// a hint that says what the choice does, as ModelFocusRow does for the focus.
// A row shows only when the model admits its option. The hint changes with the
// choice, so it is a polite live region. The comeback's word on the old MAX
// stays out of it: it follows the fit, and a change of level must not reread it.
export function ModelWizardOptions({
  theme,
  axes,
  options,
  onChange,
  ignoreOldMax,
  hintStyle,
}: {
  theme: UiTheme;
  axes: ModelAxes["options"];
  options: ModelOptionChoices;
  onChange: (options: ModelOptionChoices) => void;
  /** The fit's word (fitModelWithOptions): the start is today's effort. */
  ignoreOldMax: boolean;
  hintStyle: StyleProp<TextStyle>;
}) {
  const { t } = useTranslation();
  const without = (key: keyof ModelOptionChoices): ModelOptionChoices => {
    const next = { ...options };
    delete next[key];
    return next;
  };
  const weeks = options.comeback?.layoffWeeks ?? NO_BREAK;
  const muscle = options.specialization?.muscle ?? NO_MUSCLE;
  const last = LAYOFF_WEEKS[LAYOFF_WEEKS.length - 1];
  const weeksA11y = (w: typeof NO_BREAK | LayoffWeeks) =>
    w === NO_BREAK
      ? t("editorV3.model.comebackNone", { defaultValue: "No break" })
      : w === last
        ? t("editorV3.model.comebackMoreA11y", { count: w, defaultValue: "{{count}} weeks off or more" })
        : t("editorV3.model.comebackA11y", { count: w, defaultValue: "{{count}} weeks off" });
  const muscleName = (m: CatalogMuscleSlug | typeof NO_MUSCLE) =>
    m === NO_MUSCLE ? t("editorV3.model.specialNone", { defaultValue: "None" }) : t(muscleKey(m));
  const comebackHint =
    weeks === NO_BREAK
      ? t("editorV3.model.comebackOffHint", { defaultValue: "Effort as the model writes it." })
      : COMEBACK_EASE[weeks].setFewer
        ? t("editorV3.model.comebackSetHint", {
            defaultValue: "Effort starts lower, with one set fewer on each exercise.",
          })
        : t("editorV3.model.comebackLightHint", { defaultValue: "Effort starts a little lower." });
  const specialHint =
    muscle === NO_MUSCLE
      ? t("editorV3.model.specialOffHint", { defaultValue: "Every muscle as the model writes it." })
      : t("editorV3.model.specialHint", {
          muscle: muscleName(muscle),
          defaultValue: "One more set on each exercise that works this muscle directly: {{muscle}}.",
        });
  return (
    <>
      {axes.layoffWeeks.length > 0 ? (
        <>
          <ChipChoiceRow
            theme={theme}
            choices={[NO_BREAK, ...axes.layoffWeeks]}
            selected={weeks}
            onSelect={(w) => onChange(w === NO_BREAK ? without("comeback") : { ...options, comeback: { layoffWeeks: w } })}
            label={(w) => (w === NO_BREAK ? weeksA11y(w) : layoffLabel(w))}
            chipA11yLabel={weeksA11y}
            groupA11yLabel={t("editorV3.model.comeback", { defaultValue: "Weeks off training" })}
            title={t("editorV3.model.comeback", { defaultValue: "Weeks off training" })}
          />
          <Text style={hintStyle} {...politeLive}>
            {comebackHint}
          </Text>
          {weeks !== NO_BREAK && ignoreOldMax ? (
            <Text style={hintStyle}>
              {t("editorV3.model.comebackOldMax", {
                defaultValue: "Pick the weight you can move today at that effort, not what you lifted before the break.",
              })}
            </Text>
          ) : null}
        </>
      ) : null}
      {axes.muscles.length > 0 ? (
        <>
          <ChipChoiceRow
            theme={theme}
            choices={[NO_MUSCLE, ...axes.muscles]}
            selected={muscle}
            onSelect={(m) =>
              onChange(m === NO_MUSCLE ? without("specialization") : { ...options, specialization: { muscle: m } })
            }
            label={muscleName}
            chipA11yLabel={muscleName}
            groupA11yLabel={t("editorV3.model.special", { defaultValue: "Muscle to bring up" })}
            title={t("editorV3.model.special", { defaultValue: "Muscle to bring up" })}
          />
          <Text style={hintStyle} {...politeLive}>
            {specialHint}
          </Text>
        </>
      ) : null}
    </>
  );
}
